import type { Components, Theme } from '@mui/material/styles';

import { varAlpha } from './utils';

/**
 * Sobrescritas de componente que dão o visual do Minimal com as regras do guia: botões
 * sem elevação (o primário é Volt Blue com texto branco em negrito; o secundário é
 * contorno na cor do texto, ou seja, Ink), cartões sem borda com sombra suave e cantos de
 * 16px, links em Deep Blue sobre fundo claro — Volt Blue puro não chega ao contraste AA
 * em texto pequeno — e em Volt Blue sobre fundo escuro, onde o contraste é confortável.
 */

const MuiCssBaseline: Components<Theme>['MuiCssBaseline'] = {
  styleOverrides: ({ vars }) => ({
    '::selection': {
      backgroundColor: varAlpha(vars.palette.primary.mainChannel, 0.24),
    },
    '@media (prefers-reduced-motion: reduce)': {
      '*, *::before, *::after': {
        animationDuration: '0.01ms !important',
        animationIterationCount: '1 !important',
        transitionDuration: '0.01ms !important',
        scrollBehavior: 'auto !important',
      },
    },
  }),
};

const MuiButton: Components<Theme>['MuiButton'] = {
  defaultProps: { disableElevation: true },
  styleOverrides: {
    root: ({ theme }) => ({
      borderRadius: 8,
      variants: [
        {
          props: { variant: 'contained', color: 'primary' },
          style: { '&:hover': { backgroundColor: theme.vars.palette.primary.dark } },
        },
        {
          props: { variant: 'outlined', color: 'inherit' },
          style: {
            borderColor: 'currentColor',
            '&:hover': {
              borderColor: 'currentColor',
              backgroundColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.08),
            },
          },
        },
      ],
    }),
    sizeLarge: { minHeight: 48, paddingLeft: 20, paddingRight: 20, fontSize: 15 },
    sizeMedium: { minHeight: 40 },
  },
};

const MuiCard: Components<Theme>['MuiCard'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      zIndex: 0,
      position: 'relative',
      boxShadow: theme.vars.customShadows.card,
      borderRadius: Number(theme.shape.borderRadius) * 2,
    }),
  },
};

const MuiPaper: Components<Theme>['MuiPaper'] = {
  defaultProps: { elevation: 0 },
  styleOverrides: {
    root: { backgroundImage: 'none' },
    outlined: ({ theme }) => ({
      borderColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.16),
    }),
  },
};

const MuiLink: Components<Theme>['MuiLink'] = {
  defaultProps: { underline: 'hover' },
  styleOverrides: {
    root: ({ theme }) => ({
      '&.MuiLink-underlineHover, &.MuiLink-underlineAlways': {
        textDecorationColor: 'currentColor',
      },
      '&.MuiTypography-colorPrimary': {
        color: theme.vars.palette.primary.dark,
        ...theme.applyStyles('dark', { color: theme.vars.palette.primary.main }),
      },
    }),
  },
};

const MuiChip: Components<Theme>['MuiChip'] = {
  styleOverrides: {
    root: { fontWeight: 600 },
  },
};

const MuiMenu: Components<Theme>['MuiMenu'] = {
  styleOverrides: {
    paper: ({ theme }) => ({
      boxShadow: theme.vars.customShadows.dropdown,
      borderRadius: Number(theme.shape.borderRadius) * 1.5,
    }),
  },
};

const MuiMenuItem: Components<Theme>['MuiMenuItem'] = {
  styleOverrides: {
    root: ({ theme }) => ({ ...theme.typography.body2, borderRadius: 6 }),
  },
};

const MuiBackdrop: Components<Theme>['MuiBackdrop'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      backgroundColor: varAlpha(theme.vars.palette.grey['900Channel'], 0.8),
    }),
    invisible: { background: 'transparent' },
  },
};

const MuiAccordion: Components<Theme>['MuiAccordion'] = {
  defaultProps: { disableGutters: true, elevation: 0 },
  styleOverrides: {
    root: {
      backgroundColor: 'transparent',
      '&::before': { display: 'none' },
    },
  },
};

export const components: Components<Theme> = {
  MuiAccordion,
  MuiBackdrop,
  MuiButton,
  MuiCard,
  MuiChip,
  MuiCssBaseline,
  MuiLink,
  MuiMenu,
  MuiMenuItem,
  MuiPaper,
};
